import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext();
const THEME_MODES = new Set(["light", "dark", "auto"]);

function getInitialMode() {
  const saved = typeof localStorage === "undefined" ? null : localStorage.getItem("theme");
  return THEME_MODES.has(saved) ? saved : "auto";
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(getInitialMode);

  const getResolved = useCallback((m) => {
    if (m === "auto") {
      if (typeof window === "undefined") return "dark";
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return m;
  }, []);

  const [resolved, setResolved] = useState(() => getResolved(mode));

  useEffect(() => {
    localStorage.setItem("theme", mode);

    const apply = () => {
      const theme = getResolved(mode);
      setResolved(theme);
      document.documentElement.setAttribute("data-theme", theme);
    };
    apply();

    if (mode === "auto") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
  }, [mode, getResolved]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolved }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
